// lib/useRecommendationsToMatkul.ts
import { useMemo } from "react";
import itemMatkulJson from "@/data/itemMatkul.json";
import categoryJson from "@/data/category.json";
import type { AvailableMatkul, CategoryData, CategoryResult, ItemMatkul, RecommendationResult, ValidNimProd } from "@/types";
import { useAuthStore } from "@/stores/useAuthStore";

export function useRecomToMatkul(): RecommendationResult | null {
    const itemMatkul = itemMatkulJson as ItemMatkul;
    const categoryData = categoryJson as CategoryData;
    const recScores = useAuthStore((s) => s.recScores);
    const user = useAuthStore((s) => s.user);

    return useMemo(() => {
        if (!user || !user.nimProd || !recScores) return null;
        const nimProd = user.nimProd as ValidNimProd;

        const availableMatkuls = recScores.reduce<Record<string, AvailableMatkul>>((acc, [id, score], index) => {
            let found;
            if (nimProd) {
                // Jika nimProd ambil dari nimProd spesifik
                found = itemMatkul[nimProd]?.[id];
            } else {
                // fallback terasi semua key di itemMatkul
                for (const key in itemMatkul) {
                    if (itemMatkul[key]?.[id]) {
                        found = itemMatkul[key][id];
                        break; // Hentikan pencarian begitu data ditemukan
                    }
                }
            }

            if (!found) return acc;

            acc[id] = {
                itemId: Number(id),
                name: found.name,
                kode: found.kode,
                sks: found.sks,
                catId: found.catId,
                wajib: found.wajib,
                prasyarat: found.prasyarat,
                jadwalSmtOrd: found.jadwalSmtOrd,
                jadwalSmtBin: found.jadwalSmtBin,
                dosen: found.dosen,
                updatedTahun: found.updatedTahun,
                updatedSmtBin: found.updatedSmtBin,
                score: score,
                rank: index + 1,
            };

            return acc;
        }, {});

        // Option in matkul modal & cat additional result
        const categoryMap: Record<number, { totalScore: number; count: number }> = {};

        for (const mk of Object.values(availableMatkuls)) {
            if (!mk || mk.catId == null) continue;
            if (!categoryMap[mk.catId]) {
                categoryMap[mk.catId] = { totalScore: 0, count: 0 };
            }
            categoryMap[mk.catId].totalScore += mk.score ?? 0;
            categoryMap[mk.catId].count += 1;
        }

        const categories: CategoryResult[] = Object.entries(categoryMap)
            .map(([category, data]) => ({
                category,
                totalScore: data.totalScore,
                count: data.count
            }))
            .sort((a, b) => b.totalScore - a.totalScore);

        const topCategoryKey = categories[0]?.category;
        const categoryMatkuls = topCategoryKey ? categoryData[nimProd][topCategoryKey] : undefined;

        return {
            matkuls: availableMatkuls,
            topCategoryKey,
            categories,
            ...(categoryMatkuls && { categoryMatkuls })
        };
    }, [recScores, user]);
}